import { type ApiClient, createApiClient } from '@rythmo/api-client';
import { SCHOOL_HEADER } from '@rythmo/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import createQueryClient from 'openapi-react-query';
import { createContext, type ReactNode, useContext, useMemo, useRef } from 'react';

import { useAuth } from '../auth/AuthProvider.js';
import type { ConsoleConfig } from '../config.js';

/**
 * The generated client (ADR-0065) under TanStack Query (ADR-0065's companion, `openapi-react-query`):
 * no response shape in this app is written by hand.
 *
 * A call made inside a school passes `inSchool(tenantId)` as its init: the header names the school
 * (ADR-0049), and because the init is part of the query key, switching school refetches.
 */
function makeHooks(client: ApiClient) {
  return {
    gateway: createQueryClient(client.gateway),
    tenant: createQueryClient(client.tenant),
  };
}

interface Api {
  hooks: ReturnType<typeof makeHooks>;
}

/** The init that names a school in `X-Rythmo-School`. */
export function inSchool(tenantId: string) {
  return { headers: { [SCHOOL_HEADER]: tenantId } };
}

const ApiContext = createContext<Api | null>(null);

export function ApiProvider({ config, children }: { config: ConsoleConfig; children: ReactNode }) {
  const { user } = useAuth();
  const token = useRef<string | undefined>(undefined);
  token.current = user?.access_token;

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          // A revoked role takes effect at the next token; refetching on focus is how the console
          // notices (`docs/product/admin-web-prd.md` §4). One retry, then the error screen.
          queries: { refetchOnWindowFocus: true, retry: 1, staleTime: 30_000 },
        },
      }),
    [],
  );

  const api = useMemo<Api>(() => {
    const origins = config.serviceOrigins;
    const client = createApiClient({
      apiDomain: config.apiDomain,
      getToken: () => token.current,
      ...(origins !== undefined && {
        baseUrl: (label) => origins[label] ?? `https://${label}.${config.apiDomain}`,
      }),
    });
    return { hooks: makeHooks(client) };
  }, [config]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
    </QueryClientProvider>
  );
}

export function useApi(): Api {
  const value = useContext(ApiContext);
  if (value === null) throw new Error('useApi outside ApiProvider');
  return value;
}
